import { useState } from 'react';
import { Switch, Tag, message } from 'antd';
import { AudioOutlined, AudioMutedOutlined } from '@ant-design/icons';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { formatNumber } from '@/i18n/format';
import { Button } from '@/components/button/button';
import { Modal } from '@/components/modal/modal';
import Dropdown from '@/components/form/dropdown/dropdown';
import TextField from '@/components/form/text-field/text-field';
import { useConferenceCall } from '@/hooks/use-conference-call/use-conference-call';
import { useCompanyPhoneNumbers } from '@/hooks/use-phone-numbers/use-phone-numbers';
import { TwilioDeviceStatus } from '@/hooks/use-twilio-device/use-twilio-device';
import styles from './conference-call-section.module.scss';

interface ConferenceCallSectionProps {
  conversationId: number | undefined;
  companyId: number | undefined;
  visitorLanguage?: string;
}

const STATUS_TAG_CONFIG: Record<
  TwilioDeviceStatus,
  { color: string; labelKey: ParseKeys<'webchat'> } | null
> = {
  idle: null,
  initializing: { color: 'blue', labelKey: 'deviceStatus.initializing' },
  ready: { color: 'blue', labelKey: 'deviceStatus.ready' },
  connecting: { color: 'orange', labelKey: 'deviceStatus.connecting' },
  connected: { color: 'green', labelKey: 'deviceStatus.connected' },
  error: { color: 'red', labelKey: 'deviceStatus.error' },
};

export function ConferenceCallSection({ conversationId, companyId, visitorLanguage }: ConferenceCallSectionProps) {
  const { t } = useTranslation(['webchat', 'common']);
  const [messageApi, contextHolder] = message.useMessage();
  const [visitorPhone, setVisitorPhone] = useState('');
  const [translatorPhone, setTranslatorPhone] = useState('');
  const [fromNumberId, setFromNumberId] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);

  const { phoneNumbers, isPending: isLoadingNumbers } = useCompanyPhoneNumbers(companyId);
  const conference = useConferenceCall(conversationId);

  const fromNumberOptions = (phoneNumbers ?? []).map((pn) => ({
    label: pn.friendly_name ? `${pn.friendly_name} (${pn.phone_number})` : pn.phone_number,
    value: pn.id,
  }));

  const canStart = !!fromNumberId && !!visitorPhone.trim() && !conference.isActive;

  const handleStartClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmStart = async () => {
    setShowConfirm(false);
    if (!fromNumberId) return;

    if (translationEnabled) {
      try {
        await conference.startConference(fromNumberId, [visitorPhone.trim()], true);
      } catch {
        messageApi.error(t('webchat:conference.errors.startTranslated'));
      }
    } else {
      const participants = [visitorPhone.trim()];
      if (translatorPhone.trim()) {
        participants.push(translatorPhone.trim());
      }

      try {
        await conference.startConference(fromNumberId, participants);
      } catch {
        messageApi.error(t('webchat:conference.errors.start'));
      }
    }
  };

  const handleEndConference = async () => {
    try {
      await conference.terminateConference();
      setVisitorPhone('');
      setTranslatorPhone('');
      setFromNumberId(null);
    } catch {
      messageApi.error(t('webchat:conference.errors.end'));
    }
  };

  const statusTag = STATUS_TAG_CONFIG[conference.browserStatus];

  return (
    <div className={styles.section}>
      {contextHolder}

      {!conference.isActive ? (
        <>
          <div className={styles.phoneInputs}>
            <div className={styles.inputRow}>
              <label>{t('webchat:conference.fromNumber')}</label>
              <Dropdown
                placeholder={t('webchat:conference.fromNumberPlaceholder')}
                options={fromNumberOptions}
                value={fromNumberId}
                onChange={(value) => setFromNumberId(value as number)}
                loading={isLoadingNumbers}
                allowClear={false}
                style={{ width: 260 }}
              />
            </div>
            <div className={styles.inputRow}>
              <label>{t('webchat:conference.visitorPhone')}</label>
              <TextField
                placeholder={t('webchat:conference.phonePlaceholder')}
                value={visitorPhone}
                onChange={(e) => setVisitorPhone(e.target.value)}
                style={{ width: 260 }}
              />
            </div>
            {!translationEnabled && (
              <div className={styles.inputRow}>
                <label>{t('webchat:conference.translatorPhone')}</label>
                <TextField
                  placeholder={t('webchat:conference.phonePlaceholderOptional')}
                  value={translatorPhone}
                  onChange={(e) => setTranslatorPhone(e.target.value)}
                  style={{ width: 260 }}
                />
              </div>
            )}
            <div className={styles.inputRow}>
              <label>{t('webchat:conference.liveTranslation')}</label>
              <div className={styles.translationToggle}>
                <Switch
                  checked={translationEnabled}
                  onChange={(checked) => {
                    setTranslationEnabled(checked);
                    if (checked) setTranslatorPhone('');
                  }}
                  size="small"
                />
                {translationEnabled && visitorLanguage && visitorLanguage !== 'en-us' && (
                  <span className={styles.translationHint}>
                    {t('webchat:conference.visitorLanguage', { language: visitorLanguage })}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              buttonVariant="primary"
              onClick={handleStartClick}
              disabled={!canStart}
              loading={conference.isStarting}
            >
              {translationEnabled
                ? t('webchat:conference.startTranslated')
                : t('webchat:conference.start')}
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.activeConference}>
          <div className={styles.statusRow}>
            {statusTag && <Tag color={statusTag.color}>{t(`webchat:${statusTag.labelKey}`)}</Tag>}
            <button
              className={clsx(styles.muteButton, conference.isMuted && styles.muted)}
              onClick={conference.toggleMute}
              aria-label={conference.isMuted ? t('webchat:mic.unmute') : t('webchat:mic.mute')}
            >
              {conference.isMuted ? <AudioMutedOutlined /> : <AudioOutlined />}
            </button>
            <Button
              buttonVariant="secondary"
              onClick={handleEndConference}
              loading={conference.isEnding}
            >
              {t('webchat:conference.end')}
            </Button>
          </div>

          {conference.callSids.length > 0 && (
            <div className={styles.participants}>
              <span>
                {t('webchat:conference.participantsDialed', {
                  value: formatNumber(conference.callSids.length),
                })}
              </span>
            </div>
          )}
        </div>
      )}

      {(conference.error || conference.browserError) && (
        <div className={styles.error}>{conference.error || conference.browserError}</div>
      )}

      <Modal
        isOpen={showConfirm}
        title={
          translationEnabled
            ? t('webchat:conference.confirm.titleTranslated')
            : t('webchat:conference.confirm.title')
        }
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmStart}
        confirmText={t('webchat:conference.confirm.ok')}
        cancelText={t('common:actions.cancel')}
      >
        {translationEnabled ? (
          <p>{t('webchat:conference.confirm.bodyTranslated')}</p>
        ) : (
          <p>
            {translatorPhone.trim()
              ? t('webchat:conference.confirm.bodyMany')
              : t('webchat:conference.confirm.bodyOne')}
          </p>
        )}
      </Modal>
    </div>
  );
}
